import React from 'react'
import { Card } from 'react-bulma-components'
import { CellProps, CellValue, Column } from 'react-table'
import Table from '../table'
import { Mission, MissionWithSummary } from '../../api/missions'
import { Link } from 'react-router-dom'
import { localDate } from '../../utils/date'
import {
  MissionAbandonedMessage,
  MissionIsCancelledMessage,
  MissionIsValidatedMessage,
} from './mission-messages'
import { SessionSlice } from '../../store/session'
import WorkerLabel from '../texts/worker-label'
import Protected from '../protected/protected'
import { getUserLabel } from '../../utils/users'
import useStore from '../../store'

interface MissionsListProps {
  missions: MissionWithSummary[]
}

/**
 * Component to display a list of missions in a table
 */
const MissionsList: React.FC<MissionsListProps> = ({ missions }) => {
  const currentUserRole = useStore(state => state.session.currentUserRole)
  const tableColumns = useListTableColumns(missions, currentUserRole)

  return (
    <Table
      columns={tableColumns}
      data={missions}
      noDataMessage="Aucune mission à afficher"
      dataTest="missions-list"
    />
  )
}

// Hook pour les colonnes du tableau

const useListTableColumns = (
  missions: MissionWithSummary[],
  currentUserRole: SessionSlice['currentUserRole'],
): Column<MissionWithSummary>[] =>
  React.useMemo(() => {
    const columns: (Column<MissionWithSummary> | false)[] = [
      {
        Header: () => <WorkerLabel />,
        id: 'user',
        accessor: (mission): CellValue<string> => mission.user.firstName,
        Cell: (data: CellProps<MissionWithSummary>) => {
          const mission = data.cell.row.original
          return (
            <>
              <Protected roles={['interimAgencyMember', 'employerMember']}>
                <Link to={`/missions/${mission._id}`}>{getUserLabel(mission.user)}</Link>
              </Protected>
              <Protected roles={['worker']}>{getUserLabel(mission.user)}</Protected>
            </>
          )
        },
      },
      currentUserRole !== 'interimAgencyMember' && {
        Header: 'Agence',
        accessor: 'interimAgency',
        Cell: (data: CellProps<MissionWithSummary>) => {
          const mission = data.cell.row.original
          if ('interimAgency' in mission) {
            return mission.interimAgency?.name
          } else return <>Non</>
        },
      },
      currentUserRole !== 'employerMember' && {
        Header: 'Employeur',
        accessor: 'employer',
        Cell: (data: CellProps<MissionWithSummary>) => {
          const mission: Mission = data.cell.row.original
          return mission.employer?.name || ' '
        },
      },
      {
        Header: 'Affectation',
        accessor: 'service',
        Cell: (data: CellProps<MissionWithSummary>) => {
          const mission = data.cell.row.original
          return mission.service.name || ' '
        },
      },
      {
        Header: 'Métier',
        accessor: (mission): CellValue<string> => mission.jobTitle.name,
      },
      {
        Header: 'Début',
        accessor: 'start',
        Cell: (data: CellProps<MissionWithSummary>) => {
          const mission = data.cell.row.original
          return localDate(mission.start)
        },
      },
      {
        Header: 'Fin',
        accessor: 'end',
        Cell: (data: CellProps<MissionWithSummary>) => {
          const mission = data.cell.row.original
          return mission.end ? localDate(mission.end) : '/'
        },
      },
      currentUserRole !== 'worker' && {
        Header: (
          <MissionIsValidatedMessage
            mission={{ isValidated: true }}
            type="icon"
            small
            iconColor={'dark'}
          />
        ),
        accessor: 'isValidated',
        Cell: (data: CellProps<MissionWithSummary>) => {
          const mission = data.cell.row.original
          return <MissionIsValidatedMessage mission={mission} type="icon" small />
        },
        width: 1,
      },
      currentUserRole !== 'worker' && {
        Header: (
          <MissionAbandonedMessage
            mission={{ isAbandoned: true }}
            type="icon"
            small
            iconColor={'dark'}
          />
        ),
        accessor: 'isAbandoned',
        Cell: (data: CellProps<MissionWithSummary>) => {
          const mission = data.cell.row.original
          return <MissionAbandonedMessage mission={mission} type="icon" small />
        },
        width: 1,
      },
      currentUserRole !== 'worker' && {
        Header: (
          <MissionIsCancelledMessage
            mission={{ isCancelled: true }}
            type="icon"
            small
            iconColor={'dark'}
          />
        ),
        accessor: 'isCancelled',
        Cell: (data: CellProps<MissionWithSummary>) => {
          const mission = data.cell.row.original
          return <MissionIsCancelledMessage mission={mission} type="icon" small />
        },
        width: 1,
      },
    ]
    // .filter(c => c) not working with TS inference
    return columns.flatMap(c => (c ? [c] : []))
  }, [missions, currentUserRole])

export default MissionsList
